import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "An elegant way to randomly change every list member in Haskell",
  "description": "",
  "published": "2012-08-13T00:00:00.000Z",
  "redirect_from": ["/blog/an-elegant-way-to-randomly-change-every-list-member-in-haskell/swizec/5060"],
  "categories": "Algorithm, Evolutionary algorithm, Haskell, Programming, Random number generation, Randomness, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Randomness is a bit of a strange concept in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{`.`}</p>
    <p>{`As I discovered writing about `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/haskell-and-randomness/swizec/4763"
      }}>{`Haskell and Randomness`}</a>{` a few weeks ago, the best way to avoid doing everything inside the IO monad is to write functions that always return the random generator.`}</p>
    <p>{`The crux of the matter is that random generators are long mathematical sequences. If you keep using the same index, you'll keep getting the same "random" value. You `}<em parentName="p">{`have`}</em>{` to make sure the random generator progresses.`}</p>
    <p>{`Increasing a number by a random value would look something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`import System.Random

addRand::(RandomGen g) => g -> Int -> (g, Int)
addRand gen x = let (a, gen') = randomR (0, 20) gen
                in (gen', x+a)
main = do
  generator <- newStdGen

  print $ snd $ addRand generator 5
  -- prints 15
`}</code></pre>
    <p>{`While this approach is great for changing a single value, it breaks down when you try to randomly change a whole list:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`  print $ map (snd . (addRand generator)) [1,1,1,1]
  -- prints [11,11,11,11]
`}</code></pre>
    <p>{`Bummer! We applied a random function to every list member. And got a list of identical values!`}</p>
    <p>{`The problem is that every time `}<em parentName="p">{`addRand`}</em>{` gets called, it uses the same random generator. This means we're always using the same number in the random generator series.`}</p>
    <p>{`We need a way to perform a fold and a map at the same time. A map, so the function is applied to every list item and we get a new list, and a fold where the generator is used as the accumulator.`}</p>
    <p>{`We might be tempted to write something like this monstrosity:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`addRand'::(RandomGen g) => g -> [Int] -> [(g, Int)]
addRand' gen [x] = [addRand gen x]
addRand' gen (x:xs) = let (gen', x') = addRand gen x
                     in (gen', x'):(addRand' gen' xs)

-- and then in main
  print $ map (snd) $ addRand' generator [1,1,1,1]
  -- prints [11,3,13,14]
`}</code></pre>
    <p>{`This works. Every number in the list is different!`}</p>
    <p>{`But the code is somewhat terrible and difficult to reason about.`}</p>
    <p>{`Luckily, Haskell has got us covered and comes with a standard function called `}<em parentName="p">{`mapAccumL`}</em>{`, which is a combination of a fold and a map:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`  print $ snd $ mapAccumL addRand generator [1,1,1,1]
  -- prints [11,3,13,14]
`}</code></pre>
    <p>{`Cleaner, less work, more obvious what's going on. And it just happens to be perfect for writing `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Evolutionary_algorithm",
        "title": "Evolutionary algorithm"
      }}>{`evolutionary algorithms`}</a>{` in Haskell. But more on that later, hopefully as early as Friday.`}</p>
    <p>{`Here's the whole code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`import System.Random
import Data.List

addRand::(RandomGen g) => g -> Int -> (g, Int)
addRand gen x = let (a, gen') = randomR (0, 20) gen
                in (gen', x+a)

addRand'::(RandomGen g) => g -> [Int] -> [(g, Int)]
addRand' gen [x] = [addRand gen x]
addRand' gen (x:xs) = let (gen', x') = addRand gen x
                     in (gen', x'):(addRand' gen' xs)

main = do
  generator <- newStdGen

  print $ snd $ addRand generator 5
  -- prints 15

  print $ map (snd . (addRand generator)) [1,1,1,1]
  -- prints [11,11,11,11]

  print $ map (snd) $ addRand' generator [1,1,1,1]
  -- prints [11,3,13,14]

  print $ snd $ mapAccumL addRand generator [1,1,1,1]
  -- prints [11,3,13,14]
`}</code></pre>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/haskell-and-randomness/swizec/4763"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/97837627_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/haskell-and-randomness/swizec/4763"
        }}>{`Haskell and randomness`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.reddit.com/comments/vi425"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_116_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.reddit.com/comments/vi425"
        }}>{`What was your "ah ha" moment with Haskell?`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.pokerlistings.com/strategy/psychology/on-randomness"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_76_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.pokerlistings.com/strategy/psychology/on-randomness"
        }}>{`On Randomness`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.pokerlistings.com/blog/random-number-generators-for-dummies"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_15_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.pokerlistings.com/blog/random-number-generators-for-dummies"
        }}>{`Random Number Generators for Dummies - Poker Blog`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=5433b6d5-80a7-4bfb-90d3-81ae0cd76716",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      